/**
 * O padrão da instituição: o manual da faculdade por cima da norma.
 *
 * Aplicar troca **só forma** — fonte, margens, recuo, lado da legenda e as peças
 * que o manual exige. O texto, as referências, as figuras e a identificação não
 * são tocados: quem já escreveu quarenta páginas não perde nada por escolher o
 * padrão da Fatec depois.
 *
 * O painel também mostra o que a pessoa mudou depois de aplicar
 * (`divergenciasDoPerfil`) e o que o manual pede e o gerador ainda não faz — sem
 * promessa que não se cumpre.
 */

import {
  POSICOES_DA_LEGENDA,
  ROTULO_POSICAO_DA_LEGENDA,
  aplicarPerfil,
  divergenciasDoPerfil,
  geometriaDoDocumento,
  perfilDoDocumento,
  perfisDoModelo,
  posicaoDaLegenda,
} from '@miolos/core';
import type { DocumentoAcademico, PosicaoDaLegenda } from '@miolos/core';
import { Campo } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function PainelPerfil({
  documento,
  editar,
}: {
  documento: DocumentoAcademico;
  editar: (mudanca: (atual: DocumentoAcademico) => DocumentoAcademico) => void;
}) {
  const perfis = perfisDoModelo(documento.modelo);
  const atual = perfilDoDocumento(documento);
  const exigencias = atual?.exigencias ?? [];
  const pendencias = atual?.pendencias ?? [];
  const mudou = divergenciasDoPerfil(documento);
  const legenda = posicaoDaLegenda(documento);
  const geometria = geometriaDoDocumento(documento);

  if (perfis.length === 0) {
    return <p className="text-xs leading-relaxed texto-suave">Este modelo não usa padrão institucional.</p>;
  }

  return (
    <div className="space-y-3">
      <Select value={atual?.id ?? ''} onValueChange={(valor) => editar((antes) => aplicarPerfil(antes, valor))}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Escolha o manual da sua instituição" />
        </SelectTrigger>
        <SelectContent>
          {perfis.map((perfil) => (
            <SelectItem key={perfil.id} value={perfil.id}>
              {perfil.sigla} — {perfil.nome}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <p className="text-xs leading-relaxed texto-suave">
        Aplicar troca fonte, margens, recuo do parágrafo e o lado da legenda da figura. O texto que você já
        escreveu não muda.
      </p>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-md border border-border/60 bg-muted/30 p-2 text-[11px]">
        <dt className="text-muted-foreground">Margens (mm)</dt>
        <dd>
          topo {geometria.margens.topo} · esq. {geometria.margens.esquerda} · dir. {geometria.margens.direita}{' '}
          · fundo {geometria.margens.fundo}
        </dd>
        <dt className="text-muted-foreground">Recuo do parágrafo</dt>
        <dd>{geometria.recuoParagrafoMm} mm</dd>
        <dt className="text-muted-foreground">Legenda da figura</dt>
        <dd>{ROTULO_POSICAO_DA_LEGENDA[legenda]}</dd>
      </dl>

      {mudou.length > 0 && atual ? (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] leading-relaxed">
          Você mexeu {mudou.join(', ')} depois de aplicar o padrão. Escolha {atual.sigla} de novo para voltar
          ao manual.
        </p>
      ) : null}

      <Campo rotulo="Legenda da figura" dica="O manual do Centro Paula Souza pede abaixo da imagem.">
        <Select
          value={legenda}
          onValueChange={(valor) =>
            editar((antes) => ({ ...antes, legendaDaFigura: valor as PosicaoDaLegenda }))
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {POSICOES_DA_LEGENDA.map((item) => (
              <SelectItem key={item} value={item}>
                {ROTULO_POSICAO_DA_LEGENDA[item]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Campo>

      {exigencias.length > 0 ? (
        <details className="rounded-md border border-border/60 p-2 text-[11px] leading-relaxed">
          <summary className="cursor-pointer font-medium">O que o manual pede ({exigencias.length})</summary>
          <ul className="mt-2 list-disc space-y-1 pl-4 texto-suave">
            {exigencias.map((linha) => (
              <li key={linha}>{linha}</li>
            ))}
          </ul>
          {pendencias.length > 0 ? (
            <p className="mt-2 rounded bg-muted/60 p-2">
              <strong>Ainda não é gerado:</strong> {pendencias.join(' ')}
            </p>
          ) : null}
          <p className="mt-2 texto-suave">Fonte: {atual?.fonteDoManual}</p>
        </details>
      ) : null}
    </div>
  );
}
